import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtINR } from "@/lib/format";
import { Network, MapPin, Percent, TrendingUp, Plus, LayoutDashboard } from "lucide-react";
import BeatPlanEditor from "@/components/distribution/BeatPlanEditor";
import DistributionDashboard from "@/components/distribution/DistributionDashboard";

function flattenChannel(nodes, depth = 0, out = []) {
  for (const n of nodes || []) {
    out.push({ ...n, __depth: depth });
    if (n.children && n.children.length) flattenChannel(n.children, depth + 1, out);
  }
  return out;
}

function ChannelTreeTab() {
  const [tree, setTree] = useState([]);
  useEffect(() => { api.get("/distribution/channel-tree").then((r) => setTree(r.data)); }, []);
  const flat = useMemo(() => flattenChannel(tree), [tree]);
  return (
    <Card className="p-5 rounded-sm">
      <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Multi-echelon channel</div>
      {flat.length === 0 ? <div className="text-muted-foreground text-sm italic p-8 text-center">No distributors / retailers yet — create parties with party_type = distributor or retailer to see the tree.</div> :
        flat.map((n) => (
          <div key={n.id} style={{ marginLeft: n.__depth * 20 }} className="border-l-2 border-accent/20 pl-3 py-1.5" data-testid={`ch-node-${n.id}`}>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[10px] font-mono px-1.5 py-0.5 bg-accent/10 text-accent uppercase rounded-sm">{n.party_type}</span>
              <b>{n.name}</b>
              {n.gstin && <span className="text-[11px] font-mono text-muted-foreground">{n.gstin}</span>}
            </div>
          </div>
        ))}
    </Card>
  );
}

function SecondarySalesTab() {
  const [rows, setRows] = useState([]);
  const [dists, setDists] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [form, setForm] = useState({ distributor_id: "", retailer_id: "", date: new Date().toISOString().slice(0, 10), lines: [{ item_name: "", qty: 1, rate: 0, discount: 0 }] });
  const load = async () => {
    setRows((await api.get("/distribution/secondary-sales")).data);
    setDists((await api.get("/distribution/distributors")).data);
    setRetailers((await api.get("/distribution/retailers")).data);
  };
  useEffect(() => { load(); }, []);
  const filteredRetailers = useMemo(() => retailers.filter((r) => !form.distributor_id || r.parent_party_id === form.distributor_id), [retailers, form.distributor_id]);
  const total = form.lines.reduce((s, l) => s + (l.qty * l.rate - (l.discount || 0)), 0);
  const setLine = (i, patch) => setForm({ ...form, lines: form.lines.map((l, j) => j === i ? { ...l, ...patch } : l) });
  const save = async () => {
    if (!form.distributor_id || !form.retailer_id) return toast.error("Pick distributor + retailer");
    try {
      await api.post("/distribution/secondary-sales", { ...form, lines: form.lines.filter((l) => l.item_name && l.qty > 0) });
      toast.success("Secondary sale recorded");
      setForm({ ...form, lines: [{ item_name: "", qty: 1, rate: 0, discount: 0 }] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <Card className="p-5 rounded-sm space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Record a secondary sale</div>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label>Distributor</Label>
            <Select value={form.distributor_id} onValueChange={(v) => setForm({ ...form, distributor_id: v, retailer_id: "" })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="ss-dist"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent>{dists.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Retailer</Label>
            <Select value={form.retailer_id} onValueChange={(v) => setForm({ ...form, retailer_id: v })} disabled={!form.distributor_id}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="ss-ret"><SelectValue placeholder={form.distributor_id ? "Pick" : "Choose distributor first"} /></SelectTrigger>
              <SelectContent>{filteredRetailers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" /></div>
        </div>
        <div className="border rounded-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] font-mono uppercase"><tr><th className="text-left p-2">Item</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Rate</th><th className="text-right p-2">Disc</th><th className="text-right p-2">Total</th></tr></thead>
            <tbody>
              {form.lines.map((l, i) => (
                <tr key={i} className="border-t">
                  <td className="p-1"><Input value={l.item_name} onChange={(e) => setLine(i, { item_name: e.target.value })} className="h-8 rounded-sm" placeholder="Item name / SKU" /></td>
                  <td className="p-1 w-20"><Input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: Number(e.target.value) })} className="h-8 rounded-sm text-right" /></td>
                  <td className="p-1 w-24"><Input type="number" value={l.rate} onChange={(e) => setLine(i, { rate: Number(e.target.value) })} className="h-8 rounded-sm text-right" /></td>
                  <td className="p-1 w-24"><Input type="number" value={l.discount} onChange={(e) => setLine(i, { discount: Number(e.target.value) })} className="h-8 rounded-sm text-right" /></td>
                  <td className="p-2 text-right font-mono tabular-nums">{fmtINR(l.qty * l.rate - (l.discount || 0))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="border-t bg-muted/20"><td colSpan="4" className="p-2 text-right font-semibold">Total</td><td className="p-2 text-right font-bold font-mono">{fmtINR(total)}</td></tr></tfoot>
          </table>
          <div className="p-2"><Button size="sm" variant="ghost" onClick={() => setForm({ ...form, lines: [...form.lines, { item_name: "", qty: 1, rate: 0, discount: 0 }] })} className="gap-1"><Plus className="w-3.5 h-3.5" /> Line</Button></div>
        </div>
        <Button onClick={save} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="ss-save">Save secondary sale</Button>
      </Card>
      <Card className="rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Recent secondary sales · {rows.length}</div>
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] font-mono uppercase"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Distributor</th><th className="text-left p-2">Retailer</th><th className="text-right p-2">Total</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="4" className="text-center py-8 text-muted-foreground italic">No secondary sales yet</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-2 font-mono">{r.date}</td>
                <td className="p-2">{dists.find((d) => d.id === r.distributor_id)?.name || "—"}</td>
                <td className="p-2">{retailers.find((x) => x.id === r.retailer_id)?.name || "—"}</td>
                <td className="p-2 text-right font-mono">{fmtINR(r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function SchemesTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "percent_off", percent_off: 0, flat_off: 0, buy_qty: 0, get_qty: 0, min_qty: 0, from_date: "", to_date: "" });
  useEffect(() => { api.get("/distribution/schemes").then((r) => setRows(r.data)); }, []);
  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try { await api.post("/distribution/schemes", form); toast.success("Scheme created"); setForm({ ...form, name: "" }); api.get("/distribution/schemes").then((r) => setRows(r.data)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <Card className="p-5 rounded-sm space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">New scheme</div>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" placeholder="Diwali 10% off" data-testid="scheme-name" /></div>
          <div><Label>Kind</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percent_off">Percent off (%)</SelectItem>
                <SelectItem value="flat_off">Flat off (₹)</SelectItem>
                <SelectItem value="buy_x_get_y">Buy X get Y free</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Minimum qty</Label><Input type="number" value={form.min_qty} onChange={(e) => setForm({ ...form, min_qty: Number(e.target.value) })} className="rounded-sm mt-1 text-right" /></div>
          {form.kind === "percent_off" && <div><Label>Percent off</Label><Input type="number" value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: Number(e.target.value) })} className="rounded-sm mt-1 text-right" /></div>}
          {form.kind === "flat_off" && <div><Label>Flat off (₹)</Label><Input type="number" value={form.flat_off} onChange={(e) => setForm({ ...form, flat_off: Number(e.target.value) })} className="rounded-sm mt-1 text-right" /></div>}
          {form.kind === "buy_x_get_y" && <>
            <div><Label>Buy qty</Label><Input type="number" value={form.buy_qty} onChange={(e) => setForm({ ...form, buy_qty: Number(e.target.value) })} className="rounded-sm mt-1 text-right" /></div>
            <div><Label>Get free</Label><Input type="number" value={form.get_qty} onChange={(e) => setForm({ ...form, get_qty: Number(e.target.value) })} className="rounded-sm mt-1 text-right" /></div>
          </>}
          <div><Label>From</Label><Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label>To</Label><Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="rounded-sm mt-1" /></div>
        </div>
        <Button onClick={save} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="scheme-save">Save scheme</Button>
      </Card>
      <Card className="rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] font-mono uppercase"><tr><th className="text-left p-2">Name</th><th className="text-left p-2">Kind</th><th className="text-right p-2">Value</th><th className="text-left p-2">Period</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="4" className="text-center py-8 text-muted-foreground italic">No schemes yet</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t"><td className="p-2 font-semibold">{r.name}</td><td className="p-2 font-mono text-xs">{r.kind}</td><td className="p-2 text-right font-mono">{r.kind === "percent_off" ? `${r.percent_off}%` : r.kind === "flat_off" ? `₹${r.flat_off}` : `${r.buy_qty}+${r.get_qty}`}</td><td className="p-2 text-xs font-mono text-muted-foreground">{r.from_date || "—"} → {r.to_date || "—"}</td></tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export default function Distribution() {
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="distribution-page">
      <PageHeader eyebrow="Channel Management" title="Distribution"
        description="Multi-echelon channel (Super-stockist → Distributor → Sub-distributor → Retailer) with secondary sales tracking, volume schemes, and beat plan." />
      <Tabs defaultValue="tree">
        <TabsList className="rounded-sm">
          <TabsTrigger value="tree" className="rounded-sm gap-1.5" data-testid="tab-tree"><Network className="w-3.5 h-3.5" /> Channel Tree</TabsTrigger>
          <TabsTrigger value="secondary" className="rounded-sm gap-1.5" data-testid="tab-secondary"><TrendingUp className="w-3.5 h-3.5" /> Secondary Sales</TabsTrigger>
          <TabsTrigger value="schemes" className="rounded-sm gap-1.5" data-testid="tab-schemes"><Percent className="w-3.5 h-3.5" /> Schemes</TabsTrigger>
          <TabsTrigger value="beat" className="rounded-sm gap-1.5" data-testid="tab-beat"><MapPin className="w-3.5 h-3.5" /> Beat Plan</TabsTrigger>
        </TabsList>
        <TabsContent value="tree" className="mt-4"><ChannelTreeTab /></TabsContent>
        <TabsContent value="secondary" className="mt-4"><SecondarySalesTab /></TabsContent>
        <TabsContent value="schemes" className="mt-4"><SchemesTab /></TabsContent>
        <TabsContent value="beat" className="mt-4"><Card className="p-8 rounded-sm text-center text-muted-foreground italic">Beat Plan editor — Phase 2. Backend endpoints (<code>/distribution/beat-plans</code>) ready.</Card></TabsContent>
      </Tabs>
    </div>
  );
}
